/**
 * Edit Task Modal Component
 * 
 * Full task editing with all fields and dependencies.
 */

import React from 'react';
import { Menu, Clock, Activity, Brain, Users, Star, X } from 'lucide-react';
import { ABLE_COLORS, CONTEXT_OPTIONS } from '../../constants';

/**
 * Dropdown for context fields (area, time of day, etc.)
 */
function ContextDropdown({ icon: Icon, label, value, options, onChange }) {
  return (
    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <Icon size={20} color={ABLE_COLORS.primary} style={{ flexShrink: 0 }} />
      <label style={{
        fontSize: '14px',
        fontWeight: '600',
        color: ABLE_COLORS.textSecondary,
        minWidth: '70px'
      }}>
        {label}
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        style={{
          flex: 1,
          padding: '12px',
          border: `2px solid ${ABLE_COLORS.borderMedium}`,
          borderRadius: ABLE_COLORS.buttonRadius,
          fontSize: '14px',
          outline: 'none',
          cursor: 'pointer',
          background: ABLE_COLORS.neutral
        }}
      >
        <option value="">None</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

/**
 * Text input with datalist for autocomplete
 */
function TextInputWithDatalist({ label, value, placeholder, options, datalistId, onChange }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: ABLE_COLORS.textSecondary,
        marginBottom: '8px'
      }}>
        {label}
      </label>
      <input
        list={datalistId}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px',
          border: `2px solid ${ABLE_COLORS.borderMedium}`,
          borderRadius: ABLE_COLORS.buttonRadius,
          fontSize: '14px',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
      <datalist id={datalistId}>
        {options?.map(option => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </div>
  );
}

/**
 * Dependency selector (blocked by / blocks)
 */
function DependencySelector({ label, emoji, selectedIds, tasks, editingTaskId, bgColor, onAdd, onRemove, showStatus }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#666',
        marginBottom: '8px'
      }}>
        {emoji} {label}
      </label>
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '8px',
        marginBottom: '8px'
      }}>
        {(selectedIds || []).map(taskId => {
          const task = tasks.find(t => t.id === taskId);
          if (!task) return null;
          return (
            <div
              key={taskId}
              style={{
                padding: '6px 12px',
                background: showStatus && task.done ? ABLE_COLORS.successBg : bgColor,
                borderRadius: '12px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {showStatus && <span>{task.done ? '✅' : '⏳'}</span>}
              {!showStatus && <span>🔒</span>}
              <span>{task.title}</span>
              <button
                onClick={() => onRemove(taskId)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  color: '#666'
                }}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
      <select
        onChange={(e) => {
          const taskId = parseInt(e.target.value);
          if (taskId) onAdd(taskId);
          e.target.value = '';
        }}
        value=""
        style={{
          width: '100%',
          padding: '8px',
          border: `2px solid ${ABLE_COLORS.borderMedium}`,
          borderRadius: '8px',
          fontSize: '13px',
          cursor: 'pointer'
        }}
      >
        <option value="">+ Add {label.toLowerCase().includes('blocked') ? 'blocking' : 'blocked'} task...</option>
        {tasks
          .filter(t => t.id !== editingTaskId && !t.done && !(selectedIds || []).includes(t.id))
          .map(t => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
      </select>
    </div>
  );
}

export default function EditTaskModal({
  editingTask,
  setEditingTask,
  tasks,
  contextOptions,
  onUpdateTask,
  onCloseEdit
}) {
  if (!editingTask) return null;

  const updateField = (field, value) => {
    setEditingTask({ ...editingTask, [field]: value });
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 2000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}
      onClick={onCloseEdit}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '24px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
          position: 'relative',
          left: 'calc(var(--chat-width, 0px) / -2)',
          transition: 'left 0.2s ease'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ 
          margin: '0 0 20px 0',
          fontSize: '20px',
          color: ABLE_COLORS.primary
        }}>
          Edit Task
        </h2>

        {/* Title */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#666',
            marginBottom: '8px'
          }}>
            Title
          </label>
          <input
            type="text"
            value={editingTask.title}
            onChange={(e) => updateField('title', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${ABLE_COLORS.borderMedium}`,
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#666',
            marginBottom: '8px'
          }}>
            Notes
          </label>
          <textarea
            value={editingTask.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${ABLE_COLORS.borderMedium}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ height: '1px', background: ABLE_COLORS.borderMedium, marginBottom: '20px' }} />

        {/* Priority */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Menu size={20} color={ABLE_COLORS.primary} style={{ flexShrink: 0 }} />
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#666', minWidth: '70px' }}>
            Priority
          </label>
          <select
            value={editingTask.priority}
            onChange={(e) => updateField('priority', e.target.value)}
            style={{
              flex: 1,
              padding: '12px',
              border: `2px solid ${ABLE_COLORS.borderMedium}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              background: 'white'
            }}
          >
            {CONTEXT_OPTIONS.priority.filter(s => s !== 'Scheduled').map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
        </div>

        {/* Deadline */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Clock size={20} color={ABLE_COLORS.primary} style={{ flexShrink: 0 }} />
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#666', minWidth: '70px' }}>
            Deadline
          </label>
          <input
            type="date"
            value={editingTask.deadline || ''}
            onChange={(e) => updateField('deadline', e.target.value || null)}
            style={{
              flex: 1,
              padding: '12px',
              border: `2px solid ${ABLE_COLORS.borderMedium}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              background: 'white'
            }}
          />
        </div>

        {/* Star */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Star 
            size={20} 
            color={editingTask.starred ? ABLE_COLORS.warm : ABLE_COLORS.primary}
            fill={editingTask.starred ? ABLE_COLORS.warm : 'none'}
            style={{ flexShrink: 0, cursor: 'pointer' }}
            onClick={() => updateField('starred', !editingTask.starred)}
          />
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#666', minWidth: '70px' }}>
            Star
          </label>
          <div style={{
            flex: 1,
            padding: '12px',
            border: `2px solid ${ABLE_COLORS.borderMedium}`,
            borderRadius: '8px',
            fontSize: '14px',
            color: editingTask.starred ? ABLE_COLORS.warm : '#666',
            background: 'white',
            cursor: 'pointer'
          }}
          onClick={() => updateField('starred', !editingTask.starred)}
          >
            {editingTask.starred ? '⭐ Starred (important/priority)' : 'Click star to mark important'}
          </div>
        </div>

        {/* Area */}
        <ContextDropdown
          icon={Menu}
          label="Area"
          value={editingTask.area}
          options={CONTEXT_OPTIONS.area}
          onChange={(value) => updateField('area', value)}
        />
        
        {/* Project, Release, Workflow */}
        <TextInputWithDatalist
          label="Project"
          value={editingTask.project}
          placeholder="Type new or select existing..."
          options={contextOptions?.project}
          datalistId="project-options"
          onChange={(value) => updateField('project', value)}
        />
        <TextInputWithDatalist
          label="Release/Phase"
          value={editingTask.release}
          placeholder="Type new or select existing..."
          options={contextOptions?.release}
          datalistId="release-options"
          onChange={(value) => updateField('release', value)}
        />
        <TextInputWithDatalist
          label="Workflow Stage"
          value={editingTask.workflow}
          placeholder="Type new or select existing..."
          options={contextOptions?.workflow}
          datalistId="workflow-options"
          onChange={(value) => updateField('workflow', value)}
        />

        <div style={{ height: '1px', background: ABLE_COLORS.borderMedium, marginBottom: '20px' }} />

        {/* Energy Contexts */}
        <ContextDropdown
          icon={Clock}
          label="Time of Day"
          value={editingTask.timeOfDay}
          options={CONTEXT_OPTIONS.timeOfDay}
          onChange={(value) => updateField('timeOfDay', value)}
        />
        <ContextDropdown
          icon={Activity}
          label="Physical"
          value={editingTask.physical}
          options={CONTEXT_OPTIONS.physical}
          onChange={(value) => updateField('physical', value)}
        />
        <ContextDropdown
          icon={Brain}
          label="Mental"
          value={editingTask.mental}
          options={CONTEXT_OPTIONS.mental}
          onChange={(value) => updateField('mental', value)}
        />
        <ContextDropdown
          icon={Users}
          label="Social"
          value={editingTask.social}
          options={CONTEXT_OPTIONS.social}
          onChange={(value) => updateField('social', value)}
        />

        <div style={{ height: '1px', background: ABLE_COLORS.borderMedium, marginBottom: '20px' }} />

        {/* Dependencies Section */}
        <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#666' }}>
          Dependencies
        </h3>

        {/* Blocked By */}
        <DependencySelector
          label="Blocked by (must finish these first):"
          emoji="🔒"
          selectedIds={editingTask.blockedBy}
          tasks={tasks}
          editingTaskId={editingTask.id}
          bgColor={ABLE_COLORS.semantic.blockedBg}
          showStatus={true}
          onAdd={(taskId) => {
            if (!editingTask.blockedBy?.includes(taskId)) {
              updateField('blockedBy', [...(editingTask.blockedBy || []), taskId]);
            }
          }}
          onRemove={(taskId) => {
            updateField('blockedBy', editingTask.blockedBy.filter(id => id !== taskId));
          }}
        />

        {/* Blocks */}
        <DependencySelector
          label="Blocks (these tasks need this done first):"
          emoji="🔓"
          selectedIds={editingTask.blocks}
          tasks={tasks}
          editingTaskId={editingTask.id}
          bgColor={ABLE_COLORS.errorBg}
          showStatus={false}
          onAdd={(taskId) => {
            if (!editingTask.blocks?.includes(taskId)) {
              updateField('blocks', [...(editingTask.blocks || []), taskId]);
            }
          }}
          onRemove={(taskId) => {
            updateField('blocks', editingTask.blocks.filter(id => id !== taskId));
          }}
        />

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button
            onClick={onCloseEdit}
            style={{
              flex: 1,
              padding: '12px',
              background: 'white',
              color: '#666',
              border: `2px solid ${ABLE_COLORS.borderMedium}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onUpdateTask(editingTask.id, editingTask)}
            disabled={!editingTask.title.trim()}
            style={{
              flex: 1,
              padding: '12px',
              background: editingTask.title.trim() 
                ? `linear-gradient(135deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)` 
                : '#CCC',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: editingTask.title.trim() ? 'pointer' : 'not-allowed',
              fontSize: '16px',
              fontWeight: '600',
              opacity: editingTask.title.trim() ? 1 : 0.5,
              boxShadow: editingTask.title.trim() ? `0 4px 12px ${ABLE_COLORS.primary}40` : 'none',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (editingTask.title.trim()) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 6px 20px ${ABLE_COLORS.primary}50`;
              }
            }}
            onMouseLeave={(e) => {
              if (editingTask.title.trim()) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 4px 12px ${ABLE_COLORS.primary}40`;
              }
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
